import type { CSSProperties, ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { MOTION_LABELS, TEXTURE_LABELS, type ResolvedTheme } from "@baze/catalog";
import { HEADING_TRACKINGS, HEADING_WEIGHTS, RADIUS_PRESETS, THEME_MOTIONS, THEME_TEXTURES, type ThemeOverrides, type WebsiteDefinition } from "@baze/schema";
import { fontStack } from "@baze/engine";
import { Alert } from "@/components/ui/alert";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { themeFor } from "@/lib/catalog";
import { useCatalog } from "@/lib/queries";
import { updateTheme } from "../model/ops";
import { ColorField, type Swatch } from "./ColorField";
import { MotionDemo, STYLE_KEYFRAMES } from "./MotionPicker";
import { PALETTES, RADIUS_PREVIEW, TEXTURE_PREVIEW, type Palette } from "./presets";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type ColorKey = "background" | "text" | "primary" | "accent";

const COLOR_KEYS: ColorKey[] = ["background", "text", "primary", "accent"];

/** Site-wide styling ("Gaya" tab): palette, fonts, headings, corners, texture — all on top of the chosen design. */
export function StylePanel({ def, apply }: { def: WebsiteDefinition; apply: Apply }) {
  const catalog = useCatalog();
  if (!catalog.data) return <Skeleton className="h-96 rounded-2xl" />;
  const base = themeFor(catalog.data, { id: def.theme.id, version: def.theme.version });
  const current = themeFor(catalog.data, def.theme);
  if (!base || !current) return <Alert tone="warning">{t.editor.style.noDesign}</Alert>;
  const o = def.theme.overrides ?? {};
  const set = (patch: Partial<ThemeOverrides>, key?: string) => apply(updateTheme(def, { overrides: patch }), key);
  const designPalette: Palette = { id: "design", name: t.editor.style.designPalette, ...pickColors(base) };

  return (
    <div className="space-y-6">
      <Group title={t.editor.style.palette.title} hint={t.editor.style.palette.hint}>
        <div className="grid grid-cols-2 gap-2">
          {[designPalette, ...PALETTES].map((p) => {
            const active = p.id === "design" ? COLOR_KEYS.every((k) => !o[k]) : COLOR_KEYS.every((k) => o[k]?.toUpperCase() === p[k].toUpperCase());
            return (
              <PaletteCard
                key={p.id}
                palette={p}
                active={active}
                fontHeading={fontStack(current.fonts.heading)}
                onPick={() => set(p.id === "design" ? { background: undefined, text: undefined, primary: undefined, accent: undefined } : { background: p.background, text: p.text, primary: p.primary, accent: p.accent })}
              />
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 @[22rem]:grid-cols-4">
          {COLOR_KEYS.map((k) => (
            <ColorField
              key={k}
              label={t.editor.style.colors[k]}
              value={current.tokens.colors[k]}
              overridden={!!o[k]}
              onReset={() => set({ [k]: undefined })}
              swatches={siteSwatches(current)}
              onChange={(hex) => set({ [k]: hex }, `theme:${k}`)}
            />
          ))}
        </div>
        {current.warnings.length ? (
          <div className="mt-3 space-y-1.5">
            {current.warnings.map((w) => (
              <Alert key={w} tone="warning">
                {w}
              </Alert>
            ))}
          </div>
        ) : null}
      </Group>

      <Group title={t.editor.style.fonts.title} hint={t.editor.style.fonts.hint}>
        <FontPairs pairs={catalog.data.fontPairs} fonts={catalog.data.fonts} current={o.fontPair ?? base.design.fontPair} designPair={base.design.fontPair} onPick={(id) => set({ fontPair: id === base.design.fontPair ? undefined : id })} />
        <div className="mt-4 space-y-3 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-900/5">
          <Row label={t.editor.style.fonts.weight}>
            <Segmented
              size="sm"
              full
              label={t.editor.style.fonts.weight}
              value={String(nearestWeight(current.tokens.heading.weight))}
              onChange={(v) => set({ headingWeight: Number(v) === nearestWeight(base.tokens.heading.weight) ? undefined : Number(v) })}
              options={HEADING_WEIGHTS.map((w) => ({ value: String(w), ariaLabel: String(w), label: <span style={{ fontFamily: fontStack(current.fonts.heading), fontWeight: w }}>Ag</span> }))}
            />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label={t.editor.style.fonts.case}>
              <Segmented
                size="sm"
                full
                label={t.editor.style.fonts.case}
                value={current.tokens.heading.case}
                onChange={(v) => set({ headingCase: v === base.tokens.heading.case ? undefined : v })}
                options={[
                  { value: "normal", label: "Aa", ariaLabel: t.editor.style.fonts.caseNormal },
                  { value: "uppercase", label: "AA", ariaLabel: t.editor.style.fonts.caseUpper },
                ]}
              />
            </Row>
            <Row label={t.editor.style.fonts.tracking}>
              <Segmented
                size="sm"
                full
                label={t.editor.style.fonts.tracking}
                value={o.headingTracking ?? "normal"}
                onChange={(v) => set({ headingTracking: v === "normal" ? undefined : v })}
                options={HEADING_TRACKINGS.map((tr) => ({
                  value: tr,
                  ariaLabel: t.editor.style.fonts.trackings[tr],
                  label: <span style={{ letterSpacing: tr === "tight" ? "-0.08em" : tr === "wide" ? "0.18em" : 0 }}>AB</span>,
                }))}
              />
            </Row>
          </div>
        </div>
      </Group>

      <Group title={t.editor.style.radius.title}>
        <div className="grid grid-cols-4 gap-2">
          {RADIUS_PRESETS.map((r) => (
            <Tile key={r} active={o.radius === r} onClick={() => set({ radius: o.radius === r ? undefined : r })} label={t.editor.style.radius.presets[r]}>
              <RadiusPreview preset={r} color={current.tokens.colors.primary} />
            </Tile>
          ))}
        </div>
      </Group>

      <Group title={t.editor.style.texture.title}>
        <div className="grid grid-cols-4 gap-2">
          {THEME_TEXTURES.map((tx) => {
            const active = (o.texture ?? base.tokens.texture ?? "none") === tx;
            return (
              <Tile key={tx} active={active} onClick={() => set({ texture: tx === (base.tokens.texture ?? "none") ? undefined : tx })} label={TEXTURE_LABELS[tx]}>
                <span className="block h-full w-full" style={{ backgroundColor: current.tokens.colors.background, color: current.tokens.colors.text, ...TEXTURE_PREVIEW[tx] }} />
              </Tile>
            );
          })}
        </div>
      </Group>

      <Group title={t.editor.style.motion.title} hint={t.editor.style.motion.hint}>
        <div className="grid grid-cols-4 gap-2">
          {THEME_MOTIONS.map((mo) => {
            const own = base.tokens.motion ?? "subtle";
            const active = (o.motion ?? own) === mo;
            return (
              <Tile key={mo} active={active} onClick={() => set({ motion: mo === own ? undefined : mo })} label={MOTION_LABELS[mo]}>
                <MotionDemo keyframes={STYLE_KEYFRAMES[mo]} color={current.tokens.colors.primary} />
              </Tile>
            );
          })}
        </div>
      </Group>

      {def.theme.overrides ? (
        <button
          type="button"
          onClick={() => apply(updateTheme(def, { overrides: null }))}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
        >
          <RotateCcw className="size-4" aria-hidden="true" />
          {t.editor.style.resetAll}
        </button>
      ) : null}
    </div>
  );
}

export const nearestWeight = (w: number) => HEADING_WEIGHTS.reduce((a, b) => (Math.abs(b - w) < Math.abs(a - w) ? b : a));

function pickColors(theme: ResolvedTheme): Record<ColorKey, string> {
  const c = theme.tokens.colors;
  return { background: c.background, text: c.text, primary: c.primary, accent: c.accent };
}

/** The site's own colours as quick picks (same for every colour field). */
export function siteSwatches(theme: ResolvedTheme): Swatch[] {
  const c = theme.tokens.colors;
  return [
    { hex: c.primary, label: t.editor.style.colors.primary },
    { hex: c.accent, label: t.editor.style.colors.accent },
    { hex: c.secondary, label: t.editor.style.colors.secondary },
    { hex: c.background, label: t.editor.style.colors.background },
    { hex: c.surface, label: t.editor.style.colors.surface },
    { hex: c.text, label: t.editor.style.colors.text },
    { hex: c.inverted, label: t.editor.style.colors.inverted },
  ];
}

function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="@container">
      <div className="mb-2.5 flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">{title}</h3>
        {hint ? <p className="truncate text-[12px] text-slate-400">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[12px] font-semibold text-slate-600">{label}</p>
      {children}
    </div>
  );
}

/** A palette as a tiny page: its background, a headline in its text colour, a button and an accent mark. */
function PaletteCard({ palette: p, active, fontHeading, onPick }: { palette: Palette; active: boolean; fontHeading: string; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className="group relative overflow-hidden rounded-xl text-left ring-1 ring-slate-900/10 transition outline-none hover:-translate-y-px hover:shadow-[0_8px_20px_rgb(15_23_42/0.12)] focus-visible:ring-2 focus-visible:ring-primary aria-pressed:ring-2 aria-pressed:ring-primary aria-pressed:ring-offset-2"
    >
      <span className="relative block h-[4.25rem] px-2.5 pt-2" style={{ background: p.background, color: p.text }}>
        <span className="absolute top-2 right-2 size-3 rotate-45 rounded-[3px]" style={{ background: p.accent }} aria-hidden="true" />
        <span className="block text-[17px] leading-none font-bold" style={{ fontFamily: fontHeading }}>
          Aa
        </span>
        <span className="mt-1 block h-1 w-12 rounded-full opacity-30" style={{ background: p.text }} aria-hidden="true" />
        <span className="absolute bottom-2 left-2.5 h-3.5 w-10 rounded-full" style={{ background: p.primary }} aria-hidden="true" />
      </span>
      <span className="flex items-center justify-between gap-1 bg-white px-2.5 py-1.5">
        <span className="truncate text-[12px] font-semibold text-slate-700">{p.name}</span>
        <span className="flex -space-x-1" aria-hidden="true">
          {[p.background, p.text, p.primary, p.accent].map((c, i) => (
            <span key={i} className="size-2.5 rounded-full ring-1 ring-white" style={{ background: c }} />
          ))}
        </span>
      </span>
    </button>
  );
}

export function FontPairs({
  pairs,
  fonts,
  current,
  designPair,
  badge = t.editor.style.fonts.fromDesign,
  onPick,
}: {
  /** Label on the pair the others are measured against (the design's, or the site's inside a section). */
  badge?: string;
  pairs: { id: string; label: string; heading: string; body: string }[];
  fonts: { id: string; family: string; fallback: string }[];
  current: string;
  designPair: string;
  onPick: (id: string) => void;
}) {
  const font = (id: string) => fonts.find((f) => f.id === id);
  // The design's own pair first, then the rest in catalog order.
  const ordered = [...pairs].sort((a, b) => Number(b.id === designPair) - Number(a.id === designPair));
  return (
    <div className="scrollbar-thin -mx-1 grid max-h-[19rem] grid-cols-2 gap-2 overflow-y-auto px-1 py-1">
      {ordered.map((p) => {
        const h = font(p.heading);
        const b = font(p.body);
        if (!h || !b) return null;
        const active = p.id === current;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p.id)}
            aria-pressed={active}
            className="relative flex min-w-0 flex-col rounded-xl bg-white p-2.5 text-left ring-1 ring-line transition outline-none hover:ring-slate-300 focus-visible:ring-2 focus-visible:ring-primary aria-pressed:bg-primary-soft/50 aria-pressed:ring-2 aria-pressed:ring-primary"
          >
            {p.id === designPair ? <span className="absolute top-2 right-2 rounded-full bg-slate-900 px-1.5 py-px text-[9.5px] font-bold tracking-wide text-white uppercase">{badge}</span> : null}
            <span className="text-[26px] leading-none text-slate-900" style={{ fontFamily: fontStack(h) }}>
              Ag
            </span>
            <span className="mt-1.5 line-clamp-2 text-[12px] leading-snug text-slate-500" style={{ fontFamily: fontStack(b) }}>
              {p.label}
            </span>
            <span className="mt-1 truncate font-mono text-[10px] text-slate-400">
              {h.family.replace(/ Variable$/, "")}
              {h.id !== b.id ? ` + ${b.family.replace(/ Variable$/, "")}` : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Tile({ active, onClick, label, children }: { active: boolean; onClick: () => void; label: string; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className="group min-w-0 text-center outline-none">
      <span
        className="block aspect-square overflow-hidden rounded-xl bg-white ring-1 ring-line transition group-hover:ring-slate-300 group-focus-visible:ring-2 group-focus-visible:ring-primary group-aria-pressed:ring-2 group-aria-pressed:ring-primary group-aria-pressed:ring-offset-2"
      >
        {children}
      </span>
      <span className={cn("mt-1 block truncate text-[11.5px] font-medium", active ? "text-slate-900" : "text-slate-500")}>{label}</span>
    </button>
  );
}

export function RadiusPreview({ preset, color }: { preset: keyof typeof RADIUS_PREVIEW; color: string }) {
  const r = RADIUS_PREVIEW[preset];
  const card: CSSProperties = { borderRadius: r.card };
  return (
    <span className="grid h-full w-full place-items-center bg-slate-50 p-2">
      <span className="flex w-full flex-col gap-1 border border-slate-200 bg-white p-1.5" style={card}>
        <span className="h-1 w-3/4 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2/3" style={{ background: color, borderRadius: r.button }} />
      </span>
    </span>
  );
}
